import {
  ShareBoard,
  ShareColumn,
  editPiece,
  keepAllRemaining,
  movePiece,
  shareColumns,
} from '@/meeting/meetingSummaryShareBoard'
import { KanbanColumn } from '@/task/components/KanbanColumn'
import { Box, Button, HStack } from '@chakra-ui/react'
import { DragDropContext, Draggable, DropResult } from '@hello-pangea/dnd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { itemPieces, ReleaseItemRow } from '../releaseBoard'
import AdminReleaseCard from './AdminReleaseCard'

interface Props {
  items: ReleaseItemRow[]
  board: ShareBoard
  onChange(board: ShareBoard): void
}

const columnColors: Record<ShareColumn, string> = {
  todo: 'gray',
  keep: 'green',
  edit: 'orange',
  drop: 'red',
}

// #77 — the four columns of the compte rendu sharing (#115), for the tickets
// of a release: only « As is » and « To adjust » reach the members.
export default function AdminReleaseBoard({ items, board, onChange }: Props) {
  const { t } = useTranslation()
  const byId = new Map(items.map((item) => [item.id, item]))
  const pieces = itemPieces(items)

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return
    onChange(
      movePiece(
        board,
        pieces,
        result.draggableId,
        result.destination.droppableId as ShareColumn,
        result.destination.index
      )
    )
  }

  const columnTitle = (column: ShareColumn) =>
    column === 'todo'
      ? t('SuperAdmin.releases.todo')
      : t(`MeetingSummaryShareModal.columns.${column}`)

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      {board.columns.todo.length > 0 && (
        <Box mb={3}>
          <Button
            size="sm"
            colorScheme="green"
            variant="outline"
            onClick={() => onChange(keepAllRemaining(board))}
          >
            {t('MeetingSummaryShareModal.keepAll')}
          </Button>
        </Box>
      )}
      <Box overflowX="auto">
        <HStack align="stretch" pb={5} minW="min-content">
          {shareColumns.map((column) => (
            <KanbanColumn
              key={column}
              id={column}
              title={columnTitle(column)}
              colorScheme={columnColors[column]}
              count={board.columns[column].length}
              stretch
            >
              {board.columns[column].map((id, index) => {
                const item = byId.get(id)
                if (!item) return null
                return (
                  <Draggable key={id} draggableId={id} index={index}>
                    {(provided, snapshot) => (
                      <AdminReleaseCard
                        ref={provided.innerRef}
                        item={item}
                        isDragging={snapshot.isDragging}
                        edited={
                          column === 'edit' ? board.edited[id] : undefined
                        }
                        onEdit={(text) => onChange(editPiece(board, id, text))}
                        mb={2}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      />
                    )}
                  </Draggable>
                )
              })}
            </KanbanColumn>
          ))}
        </HStack>
      </Box>
    </DragDropContext>
  )
}
